import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FileText, HousePlus, Shield, AlertCircle, LucideIcon } from 'lucide-react';
import useThemeStore from '../store/themeStore';
import { ThemeProps } from '../utils/theme';
import { isEmbeddedInFrame } from '../utils/isEmbeddedInFrame';
import { sourcingFeeExcludingVat } from '../utils/sourcingFeeTerms';
import { buildSourcingFeeClauses } from './termsSourcingFeeClauses';
import backgroundPattern from '../images/vesta/vesta-property-pattern-01-teal-bg.svg';

interface SectionProps {
  icon: LucideIcon;
  title: string;
  children: React.ReactNode;
  className?: string;
}

interface TermsConditionsProps {
  isModal?: boolean;
}

const TermsConditions = ({ isModal = false }: TermsConditionsProps) => {
  const getThemes = useThemeStore((state) => state.theme as unknown as ThemeProps);
  const sourcingFeeClauses = buildSourcingFeeClauses(sourcingFeeExcludingVat(getThemes));
  const [isInIframe, setIsInIframe] = useState(false);

  useEffect(() => {
    setIsInIframe(isEmbeddedInFrame());
  }, []);

  const isWhiteOrShade = (color: string): boolean => {
    const s = color.trim().toLowerCase();
    if (s === "white" || s === "#ffffff" || s === "#fff" || s === "rgb(255, 255, 255)" || s === "rgba(255, 255, 255, 1)") {
      return true;
    }
    if (s.startsWith("#") && s.length === 7) {
      const hex = s.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 240;
    }
    if (s.startsWith("rgb")) {
      const matches = s.match(/\d+/g);
      if (matches && matches.length >= 3) {
        const r = parseInt(matches[0]);
        const g = parseInt(matches[1]);
        const b = parseInt(matches[2]);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness > 240;
      }
    }
    return false;
  };

  const getContrastColor = (backgroundColor: string): string => {
    return isWhiteOrShade(backgroundColor) ? "#1f2937" : "#ffffff";
  };

  const Section = ({ icon: Icon, title, children, className = "" }: SectionProps) => {
    if (isModal) {
      return (
        <div className={`bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 ease-in-out ${className}`}>
          <div className="px-4 py-3" style={{ background: getThemes?.color1 }}>
            <div className="flex items-center">
              <Icon className="w-5 h-5 mr-2" style={{ color: getContrastColor(getThemes?.color1 || "") }} />
              <h2 className="text-lg font-semibold" style={{ color: getContrastColor(getThemes?.color1 || "") }}>
                {title}
              </h2>
            </div>
          </div>
          <div className="p-4 text-sm text-gray-700">
            {children}
          </div>
        </div>
      );
    }

    return (
      <div className={`bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl hover:bg-gray-50 transition-all duration-300 ease-in-out ${className}`}>
        <div className="px-6 py-4" style={{ background: getThemes?.color1 }}>
          <div className="flex items-center">
            <Icon className="w-6 h-6 mr-3" style={{ color: getContrastColor(getThemes?.color1 || "") }} />
            <h2 className="text-xl font-bold" style={{ color: getContrastColor(getThemes?.color1 || "") }}>
              {title}
            </h2>
          </div>
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    );
  };

  const renderContent = () => (
    <div className={`mx-auto ${isModal ? 'space-y-2' : 'space-y-8'} ${isModal ? 'max-w-none' : 'max-w-4xl'}`}>
      {/* Header - Only show on full page */}
      {!isModal && (
        <div className="text-center bg-white p-6 mb-8 rounded-lg shadow-lg mt-24">
          <h1 className="text-4xl font-bold mb-2 text-gray-900">
            Terms & Conditions
          </h1>
          <p className="text-lg text-gray-700">
            Property Deal Sourcing
          </p>
        </div>
      )}

      {/* Introduction */}
      <div className={`${isModal ? 'bg-gray-50 rounded-lg p-4' : 'bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 ease-in-out'}`}>
        <div className={`flex flex-col items-center text-center ${isModal ? 'gap-2 mb-3' : 'gap-4 mb-6'}`}>
          <div
            className={`${isModal ? 'p-2 rounded-lg' : 'p-3 rounded-xl'}`}
            style={{ backgroundColor: getThemes?.color1 }}
          >
            <FileText className={`${isModal ? 'w-5 h-5' : 'w-8 h-8'}`} style={{ color: getContrastColor(getThemes?.color1 || "") }} />
          </div>
          <div>
            <p className={`text-gray-600 ${isModal ? 'text-sm' : 'text-lg'}`}>
              These Terms and Conditions ("Terms") govern the relationship between us, a UK-based property sourcing company ("we", "us", or "our"), and you, the client ("you" or "the Client"). By reserving a property through us, you agree to be bound by the following Terms.
            </p>
          </div>
        </div>
      </div>

      {/* Section 1: Reservation Fee */}
      <Section icon={HousePlus} title="1. Reservation Fee">
        <div className="space-y-3">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold">1.1.</span> To reserve a property and remove it from our listings, you must pay a refundable reservation fee of £100.
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold">1.2.</span> The reservation will hold the property for 14 calendar days, giving you the opportunity to conduct due diligence and decide whether to proceed with the purchase.
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold">1.3.</span> If you choose not to proceed, the £100 reservation fee will be refunded in full, provided you notify us before the end of the 14-day period. Allowances made if the property is unable to be viewed within this time.
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold">1.4.</span> If no decision is communicated within the 14-day period, the property will be re-listed and the reservation fee will be forfeited.
            </p>
          </div>
        </div>
      </Section>

      {/* Section 2: Sourcing Fee */}
      <Section icon={FileText} title="2. Sourcing Fee">
        <div className="space-y-3">
          {sourcingFeeClauses.map(({ number, paragraphs }) => (
            <div key={number} className="bg-gray-50 rounded-lg p-4 space-y-2">
              {paragraphs.map((paragraph, index) => (
                <p key={index} className="text-gray-700 leading-relaxed">
                  {index === 0 && <span className="font-semibold">{number}</span>} {paragraph}
                </p>
              ))}
            </div>
          ))}
        </div>
      </Section>

      {/* Section 3: General Conditions */}
      <Section icon={Shield} title="3. General Conditions">
        <div className="space-y-3">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold">3.1.</span> We do not provide financial, mortgage, tax, or legal advice. Clients are strongly advised to carry out their own due diligence and seek professional advice as required.
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold">3.2.</span> We reserve the right to amend these Terms at any time. Updated versions will be provided upon request or via our website.
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold">3.3.</span> All fees are stated in GBP and are exclusive of VAT unless otherwise specified.
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold">3.4.</span> These Terms shall be governed by and construed in accordance with the laws of England and Wales.
            </p>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <div className={`${isModal ? 'bg-gray-50 rounded-lg p-4 mt-4' : 'bg-white rounded-xl shadow-lg p-6 mt-8 hover:shadow-2xl transition-all duration-300 ease-in-out'}`}>
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <AlertCircle className="w-4 h-4 text-gray-500" />
            <p className={`text-gray-500 ${isModal ? 'text-xs' : 'text-sm'}`}>
              By reserving a property through us, you confirm that you have read, understood, and agree to these Terms and Conditions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (isModal || isInIframe) {
    return (
      <div className="p-6" style={{ backgroundColor: 'white' }}>
        <Helmet>
          <title>Terms & Conditions | {getThemes?.name || 'Property Investment Platform'}</title>
          <meta name="description" content="Our terms and conditions for property deal sourcing" />
        </Helmet>
        {renderContent()}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Terms & Conditions | {getThemes?.name || 'Property Investment Platform'}</title>
        <meta name="description" content="Our terms and conditions for property deal sourcing" />
      </Helmet>

      {/* Background Pattern */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-5"
        style={{ backgroundImage: `url(${backgroundPattern})` }}
      />

      {/* Main Content */}
      <div className="relative z-10 flex-1 py-12 px-4 sm:px-6 lg:px-8">
        {renderContent()}
      </div>
    </div>
  );
};

export default TermsConditions;
